import React, { useEffect, useState } from 'react'
import { ElementData, ElementDataValue, EntityData, FormElement } from '../../lib/types'
import ChangeIndicator from './change-indicator'
import { entities } from '../../lib/api'
import EntitySearch from '../entity-search'
import { Tooltip } from '@carbon/react'
import { Information } from '@carbon/icons-react'
import { getCaseInsensitive } from '../../lib/get-id'

type Props = {
    original: ElementData
    data: ElementData
    prevData?: ElementData
    element: FormElement
    onChange: (changes: Record<string, ElementDataValue>) => void
}

export default function MagicPointerElement(props: Props) {
    const { element, data, prevData, original, onChange } = props
    const [text, setText] = useState('')
    const [loaded, setLoaded] = useState(false)
    const [err, setErr] = useState('')

    let datasetValue = getCaseInsensitive(data, element.datasetField || '')
    let idValue = getCaseInsensitive(data, element.field || '')
    let changed =
        datasetValue !== getCaseInsensitive(original, element.datasetField || '') ||
        idValue !== getCaseInsensitive(original, element.field || '') ||
        (prevData ? idValue !== getCaseInsensitive(prevData, element.field || '') : false)

    // Load the name if we're starting with the dataset and id
    useEffect(() => {
        if (loaded) return
        setLoaded(true)
        if (datasetValue && idValue) {
            let entityType = element.entities?.find((e) => e.dataset === datasetValue)
            if (!entityType) return
            entities
                .get(entityType, Number(idValue))
                .then((e) => setText(e.name))
                .catch((err) => setErr(err.message || err))
        }
    }, [datasetValue, idValue, element, loaded])

    function optionClick(entity: EntityData) {
        setText(entity.name)
        let dataset = element.entities![entity._entityIndex!].dataset
        onChange({
            [element.field!]: Number(entity.id),
            [element.datasetField!]: dataset,
        })
        setErr('')
    }

    function textChange(text: string) {
        setText(text)
        if (text === '')
            onChange({
                [element.field!]: null,
                [element.datasetField!]: null,
            })
    }

    return (
        <div className='mt-2'>
            <label className='font-bold text-blue-700'>
                {element.label || element.field}
                {element.required && <span className='text-red-300 mx-1'>*</span>}
                {!element.readonly && <ChangeIndicator changed={changed} />}
                {changed && prevData && (
                    <Tooltip
                        align='bottom'
                        label={getCaseInsensitive(prevData, element.field || '') + ' → ' + getCaseInsensitive(data, element.field || '')}
                    >
                        <span className='cursor-pointer ml-1 flex flex-row items-center'>
                            <Information />
                        </span>
                    </Tooltip>
                )}
            </label>
            {err && <div className='text-red-500'>{err}</div>}
            <EntitySearch
                entities={element.entities!}
                text={text}
                textChange={textChange}
                readonly={!!element.readonly}
                onErr={setErr}
                optionClick={optionClick}
                style={{ borderColor: changed ? 'Highlight' : '#ADB0B3' }}
            />
        </div>
    )
}
